<template>
  <div
    v-show="!isCustomerRole"
    class="menu-support-widget"
  >
    <!-- Collapsed trigger button -->
    <button
      v-if="!store.isOpen"
      type="button"
      :aria-label="$t('common.menuSupport.openHelp')"
      class="bg-primary bottom-6 cursor-pointer fixed font-semibold gap-2 hover:bg-primary/90 hover:shadow-xl inline-flex items-center px-5 py-3 right-6 rounded-full shadow-lg text-primary-content text-sm transition z-(--z-index-ds-sticky)"
      @click="handleOpen"
    >
      <HelpCircleIcon class="h-5 w-5" />{{ $t('commerce.menuSupportWidget.menuHelp') }}
    </button>

    <!-- Support drawer using shared BaseDrawer -->
    <BaseDrawer
      v-model="drawerModel"
      :title="$t('common.menuSupport.title')"
      :description="$t('common.menuSupport.description')"
      max-width-class="max-w-md"
      close-on-outside-click
      blur-backdrop
      bottom-sheet-on-mobile
    >
      <MenuSupportForm @send-requested="handleSubmit" />
    </BaseDrawer>
  </div>
</template>

<script setup lang="ts">
import { HelpCircleIcon } from '@lucide/vue'
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useUserPermissions } from '~/composables/useUserPermissions'
import { useMenuSupportStore } from '~/stores/useMenuSupportStore'
import type { MenuSupportRequest } from '~/services/api/generated'
import MenuSupportForm from '~/components/menu/MenuSupportForm.vue'
import BaseDrawer from '~/components/shared/dialogs/BaseDrawer.vue'
import { Normalization } from '~/utils/normalization'

const store = useMenuSupportStore()
const route = useRoute()
const { isCustomerRole } = useUserPermissions()

// Guarded v-model bridge: route updates through handleOpen/handleClose
const drawerModel = computed({
  get: () => store.isOpen,
  set: (value: boolean) => {
    if (value) {
      handleOpen()
    } else {
      handleClose()
    }
  }
})

function handleOpen() {
  if (store.isOpen) return

  // Pre-fill from auth user if available
  const { identity } = useIdentity()
  const current = identity.value
  if (current !== null) {
    const prefill: { name?: string; email?: string } = {}
    if (current.name !== null) prefill.name = current.name
    if (current.email !== null) prefill.email = current.email
    store.prefill(prefill)
  }
  store.open()
}

function handleClose() {
  if (!store.isOpen) return
  store.close()
}

async function handleSubmit(payload: MenuSupportRequest) {
  const routeMenuId = Array.isArray(route.params['id']) ? route.params['id'][0] : route.params['id']
  const menuId = Normalization.intValue(routeMenuId, null) ?? undefined

  await store.submit({
    ...payload,
    ...(menuId === undefined ? {} : { menu_id: menuId }),
    current_url: window.location.href
  })
}

// Close on route change
watch(
  () => route.fullPath,
  () => {
    if (store.isOpen) {
      store.close()
    }
  }
)
</script>
